import { AppDate } from '@macs-frontend/core/utils/date';
import { UUID } from '@macs-frontend/core/utils/uuid';
import { ActionPlanningItemState } from '@macs-gql';
import { AbstractLocation } from './abstract-location';
import { MdBaseAction } from './action';
import { BaseActionTeam, BaseATTransport, BaseCompositionRole } from './action-team';
import { BaseChannel } from './channel.model';

export enum APTogglableBlockTypes {
  ActionTeams = 'ActionTeams',
  Distribute = 'Distribute',
  Details = 'Details',
}

export type APTogglableBlocks = {
  [key in keyof typeof APTogglableBlockTypes]: boolean;
};

export interface Prices {
  billablePrice: number;
  unitPrice: number;
  totalPricePerMaterial: number;
}

export interface StringDateRange {
  startDate: null | AppDate;
  endDate: null | AppDate;
}

export interface MomentDateRange {
  startDate: Date;
  endDate: Date;
}

export interface BaseActionPlanningItem {
  uuid: UUID;
  startDate: null | AppDate;
  endDate: null | AppDate;
  name: string;
  numberOfActions: number;
  state: ActionPlanningItemState;
}

export interface ActionPlanningItemActionTeamChannel extends Pick<BaseChannel, 'name'> {}

export interface ActionPlanningItemActionTransport
  extends Pick<BaseATTransport, 'name'> {}

export interface ActionPlanningItemActionTeam
  extends Pick<
    BaseActionTeam,
    | 'uuid'
    | 'name'
    | 'viaCompanyStartDate'
    | 'viaCompanyEndDate'
    | 'directToEventStartDate'
    | 'directToEventEndDate'
    | 'startDate'
    | 'endDate'
  > {
  channel: ActionPlanningItemActionTeamChannel;
  transports: ActionPlanningItemActionTransport[];
}

export interface ActionPlanningItemDateActionLocation
  extends Pick<AbstractLocation, 'name' | 'address'> {}

export interface ActionPlanningItemDateActionTeamChannel
  extends Pick<BaseChannel, 'uuid' | 'name'> {}

export interface ActionPlanningItemDateActionTeamCompositionRole
  extends Pick<BaseCompositionRole, 'roleName' | 'quantity'> {}

export interface ActionPlanningItemDateActionTeamTransport
  extends Pick<BaseATTransport, 'name'> {}

export interface ActionPlanningItemDateActionTeam
  extends Pick<
    BaseActionTeam,
    | 'uuid'
    | 'viaCompanyStartDate'
    | 'viaCompanyEndDate'
    | 'directToEventStartDate'
    | 'directToEventEndDate'
    | 'startDate'
    | 'endDate'
    | 'type'
    | 'name'
    | 'customNumber'
  > {
  channel: ActionPlanningItemDateActionTeamChannel;
  compositionRoles: ActionPlanningItemDateActionTeamCompositionRole[];
  transports: ActionPlanningItemDateActionTeamTransport[];
}

export interface ActionPlanningItemDateAction
  extends Pick<MdBaseAction, 'uuid' | 'notes' | 'bookingPersonsFullness' | 'name'> {
  actionTeam: ActionPlanningItemDateActionTeam;
  actionLocation?: ActionPlanningItemDateActionLocation;
}

export interface ActionPlanningItemDate extends BaseActionPlanningDateAction {
  action?: ActionPlanningItemDateAction;
}

export interface ActionPlanningItem extends BaseActionPlanningItem {
  actionPlanningDates: ActionPlanningItemDate[];
  actionTeam: ActionPlanningItemActionTeam;
}

export interface ActionPlanningItemPayload {
  actionTeam: UUID;
  uuid: UUID;
  startDate: null | AppDate;
  endDate: null | AppDate;
  name: string;
  numberOfActions: number;
  actionPlanningDates: { [uuid: string]: ActionPlanningItemDate };
}

export interface ActionTeamsTotalsDictionary {
  [actionTeam: string]: ActionTeamTotals;
}

export interface ActionTeamTotals {
  billablePrice: number;
  totalBillablePrice: number;
  unitPrice: number;
  totalUnitPrice: number;
  extraCost: number;
  percent: number;
  actions: number;
  plannedActions: number;
  contacts: number;
  ccBillable: number;
}

export interface TotalBudget {
  billable: number;
  unit: number;
}

export interface ActionPlanningDayActionsDictionary {
  [date: string]: number;
}

export interface ActionPlanningDayMaterialsDictionary {
  [projectMaterialId: string]: {
    [date: string]: number;
  };
}

export interface DisposableMaterialsTotalsDictionary {
  [projectMaterialId: string]: DisposableMaterialTotals;
}

export interface DisposableMaterialTotals {
  estimated: number;
  transported: number;
}

export interface ActionPlanningItemsDictionary {
  [actionTeam: string]: ActionPlanningItemDictionary;
}

export interface ActionPlanningItemDictionary {
  item: ActionPlanningItem;
  dates: ActionPlanningItemDictionaryDates;
}

export interface ActionPlanningItemDictionaryDates {
  [date: string]: ActionPlanningDate;
}

export interface ActionPlanningDate {
  uuid?: UUID;
  date: string;
  numberOfActions: number;
  dateActions: ActionPlanningItemDate[];
  actionPlanningItem?: ActionPlanningItem;
}

export interface BaseActionPlanningDateAction {
  uuid?: UUID;
  date: AppDate;
  actionPlanningItemId?: UUID;
}

export interface ActionPlanningDateAction extends BaseActionPlanningDateAction {
  actionPlanningItem?: BaseActionPlanningItem;
}

export interface ActionPlanningCalendar {
  startDate: string;
  endDate: string;
  items: readonly ActionPlanningWeek[];
}

export interface ActionPlanningWeek {
  name: string;
  weekOfYear: number;
  dates: AppDate[];
  isCurrent: boolean;
  isActive: boolean;
  lazyLoaded: boolean;
  uuid: UUID;
}
